/*
 * Purpose: Mission request field catalog — AI prompt + runtime validation
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { hasResolvedSecret } from '@/lib/resolveSecret';
import { useSecretStore } from '@/stores/secretStore';
import type { MissionKnowledgeField, MissionKnowledgeRequest } from '@/services/mission-knowledge/types';
import { VAULT_FIELD_MARKER } from '@/services/mission-knowledge/types';

/** Exported for LLM system prompts — keep in sync with MissionRequestField renderer. */
export const MISSION_REQUEST_FIELD_CATALOG = `
Mission request page field types (compose knowledgeRequests[].fields[]):
- text, textarea, number, email, url, phone, date — storage: mission (default)
- select, multiselect — options[] required
- boolean — checkbox / switch
- tags — comma-separated labels
- rating — 1–5 or 1–10 (maxRating)
- file_text — paste document body (long text, any content); large textarea
- vault_secret — sensitive value → encrypted device vault ONLY (storage: vault, vaultSecretName e.g. JOB_SITE_PASSWORD)
- info — read-only callout (body text, not an input)

Each knowledgeRequests[] block: { id, gapRef, title, description?, layout?: "form"|"callout", fields[] }
Use vault_secret for passwords/tokens/API keys the Mission needs; use file_text/textarea for domain docs.
Never put secret values in mission storage or gaps text — only vault_secret fields.
`.trim();

export function fieldStorage(field: MissionKnowledgeField): 'mission' | 'vault' {
  if (field.type === 'vault_secret' || field.storage === 'vault') return 'vault';
  return 'mission';
}

export function vaultKeyForField(field: MissionKnowledgeField): string {
  return (field.vaultSecretName || field.id).trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_');
}

export function vaultSecretConfigured(secretName: string): boolean {
  if (hasResolvedSecret(secretName)) return true;
  const store = useSecretStore.getState();
  if (store.hasSecret(secretName)) return true;
  return false;
}

export function fieldHasValue(field: MissionKnowledgeField, answers: Record<string, string>): boolean {
  if (field.type === 'info') return true;

  if (fieldStorage(field) === 'vault') {
    const key = vaultKeyForField(field);
    return vaultSecretConfigured(key) || answers[field.id]?.startsWith(VAULT_FIELD_MARKER) === true;
  }

  const v = (answers[field.id] ?? '').trim();
  if (field.type === 'boolean') return v === 'true' || v === 'false';
  if (field.type === 'multiselect') {
    try {
      const arr = JSON.parse(v || '[]') as unknown;
      return Array.isArray(arr) && arr.length > 0;
    } catch {
      return v.length > 0;
    }
  }
  return v.length > 0;
}

export function fieldIsComplete(field: MissionKnowledgeField, answers: Record<string, string>): boolean {
  if (field.type === 'info') return true;
  if (!field.required) return true;
  return fieldHasValue(field, answers);
}

export function requestIsComplete(
  request: MissionKnowledgeRequest,
  answers: Record<string, string>
): boolean {
  const inputFields = request.fields.filter((f) => f.type !== 'info');
  const required = inputFields.filter((f) => f.required);
  if (required.length === 0) {
    return inputFields.some((f) => fieldHasValue(f, answers));
  }
  return required.every((f) => fieldIsComplete(f, answers));
}

export function formatStructuredAnswersForContext(
  requests: MissionKnowledgeRequest[],
  answers: Record<string, string>
): string {
  const blocks: string[] = [];
  for (const req of requests) {
    const lines = req.fields
      .map((f) => {
        if (f.type === 'info') return f.body ? `- Note: ${f.body}` : null;
        if (fieldStorage(f) === 'vault') {
          const key = vaultKeyForField(f);
          if (!vaultSecretConfigured(key) && !answers[f.id]?.startsWith(VAULT_FIELD_MARKER)) return null;
          return `- ${f.label}: [stored in vault as ${key}]`;
        }
        const v = (answers[f.id] ?? '').trim();
        if (!v) return null;
        if (f.type === 'boolean') return `- ${f.label}: ${v === 'true' ? 'yes' : 'no'}`;
        if (f.type === 'multiselect') {
          try {
            const arr = JSON.parse(v) as string[];
            return `- ${f.label}: ${arr.join(', ')}`;
          } catch {
            return `- ${f.label}: ${v}`;
          }
        }
        if (f.type === 'file_text' && v.length > 2000) {
          return `- ${f.label}: ${v.slice(0, 2000)}…`;
        }
        return `- ${f.label}: ${v}`;
      })
      .filter(Boolean);
    if (lines.length > 0) {
      blocks.push(`### ${req.title}\n${lines.join('\n')}`);
    }
  }
  return blocks.join('\n\n');
}

export function normalizeAiField(raw: Partial<MissionKnowledgeField> & { id: string; label: string }): MissionKnowledgeField | null {
  const type = raw.type ?? 'textarea';
  const allowed = [
    'text', 'textarea', 'number', 'email', 'url', 'date', 'phone',
    'select', 'multiselect', 'boolean', 'tags', 'rating', 'file_text', 'vault_secret', 'info',
  ] as const;
  const safeType = allowed.includes(type as (typeof allowed)[number])
    ? (type as MissionKnowledgeField['type'])
    : 'textarea';

  return {
    id: raw.id,
    label: raw.label,
    type: safeType,
    placeholder: raw.placeholder,
    helpText: raw.helpText,
    required: raw.required,
    options: raw.options,
    storage: raw.storage ?? (safeType === 'vault_secret' ? 'vault' : 'mission'),
    vaultSecretName: raw.vaultSecretName,
    maxRating: raw.maxRating,
    body: raw.body,
  };
}
